import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../environments/environment';
import { ProgrammingBoardItem, ProgrammingClientEvent, ProgrammingStatusInfo, ProgrammingStatusKey, ProgrammingTrends } from '../models/programming-workflow';

@Injectable({
  providedIn: 'root'
})
export class ProgrammingWorkflowService {

  baseUrl = environment.apiEndpoint + '/programming/';

  // Fires with a project id when something on the page may have moved its status server-side,
  // so the section header can refresh without the SP Flow / QA Flow components knowing about it.
  private statusChangedSource = new Subject<string>();
  statusChanged$ = this.statusChangedSource.asObservable();

  // Fires with a project id when the Programming header's Survey setup saved the links, platform
  // or programming team, so QA Flow can refresh what it derived from them.
  private setupChangedSource = new Subject<string>();
  setupChanged$ = this.setupChangedSource.asObservable();

  constructor(private http: HttpClient) { }

  getBoard(): Observable<ProgrammingBoardItem[]> {
    return this.http.get<ProgrammingBoardItem[]>(`${this.baseUrl}board`);
  }

  getTrends(weeks: number): Observable<ProgrammingTrends> {
    return this.http.get<ProgrammingTrends>(`${this.baseUrl}trends`, { params: { weeks } });
  }

  getStatus(projectId: string): Observable<ProgrammingStatusInfo> {
    return this.http.get<ProgrammingStatusInfo>(`${this.baseUrl}${projectId}/status`);
  }

  // Moves the status forward to match what the project already has (links, documents, QA runs).
  syncStatus(projectId: string): Observable<ProgrammingStatusInfo> {
    return this.http.post<ProgrammingStatusInfo>(`${this.baseUrl}${projectId}/sync`, {});
  }

  setStatus(projectId: string, status: ProgrammingStatusKey): Observable<ProgrammingStatusInfo> {
    return this.http.put<ProgrammingStatusInfo>(`${this.baseUrl}${projectId}/status`, { status });
  }

  reportEvent(projectId: string, eventName: ProgrammingClientEvent): Observable<ProgrammingStatusInfo> {
    return this.http.post<ProgrammingStatusInfo>(`${this.baseUrl}${projectId}/events`, { eventName });
  }

  // Fire-and-forget: a failed status report must never surface as an error on the comment or
  // approval that triggered it.
  reportEventQuietly(projectId: string, eventName: ProgrammingClientEvent): void {
    if (!projectId) return;
    this.reportEvent(projectId, eventName).subscribe({
      next: () => this.notifyStatusMayHaveChanged(projectId),
      error: () => { }
    });
  }

  notifyStatusMayHaveChanged(projectId: string): void {
    this.statusChangedSource.next(projectId);
  }

  notifySetupChanged(projectId: string): void {
    this.setupChangedSource.next(projectId);
  }
}
